import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Maximize2,
  MapPin,
  DollarSign,
  Layers,
  Droplets,
  Zap,
  Wifi,
  Flame,
  Building2,
  MessageCircle,
  CheckCircle2,
  XCircle,
  Users,
  Phone,
  Grid3X3,
  ImageIcon
} from 'lucide-react';
import { useProject } from '../../../contexts/ProjectContext';
import WhatsAppModal from '../../../components/WhatsAppModal';
import brandConfig from '../../../config/brandConfig';
import { Badge } from '../../../components/ui';
import { cn } from '../../../lib/utils';
import { getCurrencySymbol } from '../../../utils/countryUtils';

const LoteTab = ({ selectedLot, onClose }) => {
  const { project, selectedLotData, lotLoading, lotError } = useProject();
  const [activeTab, setActiveTab] = useState('general');
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const lotInfo = selectedLotData || selectedLot;
  const lotProperties = lotInfo?.properties || lotInfo?.properties;
  const lotId = lotInfo?.id || lotInfo?.properties?.id;

  const services = [
    { key: 'has_water', name: 'Agua', icon: Droplets, color: 'text-blue-500' },
    { key: 'has_electricity', name: 'Electricidad', icon: Zap, color: 'text-yellow-500' },
    { key: 'has_sewage', name: 'Alcantarillado', icon: Building2, color: 'text-gray-500' },
    { key: 'has_gas', name: 'Gas', icon: Flame, color: 'text-orange-500' },
    { key: 'has_internet', name: 'Internet', icon: Wifi, color: 'text-purple-500' }
  ];

  const getServiceValue = (serviceKey) => {
    if (lotProperties && lotProperties[serviceKey] !== undefined) {
      return lotProperties[serviceKey];
    }
    if (project && project[serviceKey] !== undefined) {
      return project[serviceKey];
    }
    return false;
  };

  const getStatusConfig = (status) => {
    const configs = {
      'DISPONIBLE': { variant: 'success', label: 'Disponible' },
      'EN_VENTA': { variant: 'error', label: 'Vendido' },
      'VENDIDO': { variant: 'error', label: 'Vendido' },
      'RESERVADO': { variant: 'warning', label: 'Reservado' },
      'NO_DISPONIBLE': { variant: 'default', label: 'No Disponible' },
      'DEVOLUCION': { variant: 'error', label: 'Vendido' }
    };
    return configs[status] || configs['NO_DISPONIBLE'];
  };

  const lotImages = lotProperties?.images || [];

  const tabs = [
    { id: 'general', label: 'General', icon: Home },
    { id: 'perimetro', label: 'Perímetro', icon: Maximize2 },
    { id: 'servicios', label: 'Servicios', icon: Layers }
  ];

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {lotLoading ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full mx-auto mb-3"
            />
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Cargando...
            </p>
          </div>
        </div>
      ) : lotError ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <p className="text-xs font-semibold text-red-600 mb-1">Error al cargar</p>
            <p className="text-[10px] text-gray-500">{lotError}</p>
          </div>
        </div>
      ) : lotInfo ? (
        <>
          {/* Header */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            {/* Close button and title row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-sm">
                  <Home className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white">
                      Lote {lotProperties?.lot_number || lotProperties?.name || '-'}
                    </h3>
                    <Badge variant={getStatusConfig(lotProperties?.status).variant} size="sm">
                      {getStatusConfig(lotProperties?.status).label}
                    </Badge>
                    {lotImages.length > 0 && (
                      <button
                        onClick={() => { setGalleryIndex(0); setIsGalleryOpen(true); }}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-orange-100 dark:hover:bg-orange-900/30 text-gray-600 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 transition-colors"
                        title="Ver fotos del lote"
                      >
                        <ImageIcon className="w-3.5 h-3.5" />
                        <span className="text-[10px] font-semibold">{lotImages.length}</span>
                      </button>
                    )}
                  </div>
                  {(lotProperties?.address || lotProperties?.block) && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      {lotProperties?.address || `Manzana ${lotProperties.block}`}
                    </p>
                  )}
                </div>
              </div>
              {onClose && (
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Cerrar"
                >
                  <XCircle className="w-5 h-5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" />
                </button>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-[10px] font-semibold rounded-md transition-all',
                      isActive
                        ? 'bg-white dark:bg-gray-800 text-orange-600 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    )}
                  >
                    <Icon className="w-3 h-3" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.15 }}
                className="p-3 space-y-2"
              >
                {activeTab === 'general' && (
                  <div className="space-y-2">
                    {project?.show_prices && (
                      <>
                        <div className="flex items-center justify-between p-2.5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
                          <div className="flex items-center gap-1.5">
                            <DollarSign className="w-4 h-4 text-green-600" />
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              Precio Total
                            </span>
                          </div>
                          <span className="text-sm font-bold text-green-600">
                            {lotProperties?.price_per_sqm && lotProperties?.area_calculated
                              ? `${getCurrencySymbol(lotProperties?.currency || project?.currency)} ${(lotProperties.price_per_sqm * lotProperties.area_calculated).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                              : lotProperties?.price
                                ? `${getCurrencySymbol(lotProperties?.currency || project?.currency)} ${lotProperties.price.toLocaleString()}`
                                : '-'}
                          </span>
                        </div>

                        <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <span className="text-[11px] text-gray-600 dark:text-gray-400">Precio por m²</span>
                          <span className="text-xs font-semibold text-gray-900 dark:text-white">
                            {lotProperties?.price_per_sqm
                              ? `${getCurrencySymbol(lotProperties?.currency || project?.currency)} ${lotProperties.price_per_sqm.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                              : lotProperties?.price && lotProperties?.area_calculated
                                ? `${getCurrencySymbol(lotProperties?.currency || project?.currency)} ${(lotProperties.price / lotProperties.area_calculated).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                                : '-'}
                          </span>
                        </div>
                      </>
                    )}

                    <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-1.5">
                        <Maximize2 className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-[11px] text-gray-600 dark:text-gray-400">Área</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">
                        {lotProperties?.area_calculated ? `${lotProperties.area_calculated.toFixed(2)} m²` : '-'}
                      </span>
                    </div>

                    {lotProperties?.typology && (
                      <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <span className="text-[11px] text-gray-600 dark:text-gray-400">Tipología</span>
                        <span className="text-xs font-semibold text-gray-900 dark:text-white">
                          {lotProperties.typology}
                        </span>
                      </div>
                    )}

                    {lotProperties?.location && (
                      <div className="flex items-start justify-between p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 mt-0.5" />
                          <span className="text-[11px] text-gray-600 dark:text-gray-400">Ubicación</span>
                        </div>
                        <span className="text-[11px] font-medium text-gray-900 dark:text-white text-right">
                          {lotProperties.location}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'perimetro' && (
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                      Medidas
                    </h4>
                    {[
                      { label: 'Frente', value: lotProperties?.front_side || lotProperties?.front_meters || lotProperties?.front_width },
                      { label: 'Derecha', value: lotProperties?.right_side || lotProperties?.perimeter_right },
                      { label: 'Fondo', value: lotProperties?.back_side || lotProperties?.back_depth || lotProperties?.depth_meters },
                      { label: 'Izquierda', value: lotProperties?.left_side || lotProperties?.perimeter_left }
                    ].filter((side) => side.value).map((side) => (
                      <div
                        key={side.label}
                        className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-700 transition-colors"
                      >
                        <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300">{side.label}</span>
                        <span className="text-xs font-semibold text-gray-900 dark:text-white">
                          {side.value}
                        </span>
                      </div>
                    ))}
                    {![lotProperties?.front_side, lotProperties?.front_meters, lotProperties?.front_width,
                      lotProperties?.right_side, lotProperties?.perimeter_right,
                      lotProperties?.back_side, lotProperties?.back_depth, lotProperties?.depth_meters,
                      lotProperties?.left_side, lotProperties?.perimeter_left].some(Boolean) && (
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 text-center py-3">
                        Sin medidas disponibles
                      </p>
                    )}
                  </div>
                )}

                {activeTab === 'servicios' && (
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                      Servicios
                    </h4>
                    {services.filter((service) => getServiceValue(service.key)).length > 0 ? (
                      services.filter((service) => getServiceValue(service.key)).map((service) => {
                        const Icon = service.icon;
                        return (
                          <div
                            key={service.key}
                            className="flex items-center justify-between p-2 rounded-lg border transition-all bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                          >
                            <div className="flex items-center gap-2">
                              <Icon className={cn('w-3.5 h-3.5', service.color)} />
                              <span className="text-[11px] font-medium text-gray-900 dark:text-white">
                                {service.name}
                              </span>
                            </div>
                            <CheckCircle2 className="w-4 h-4 text-success-600" />
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center py-3">
                        Sin servicios registrados
                      </p>
                    )}
                  </div>
                )}

              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer - WhatsApp Contact Options (solo para lotes disponibles) */}
          {lotProperties?.status === 'DISPONIBLE' && (
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-2">
            <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
              Contactar por WhatsApp
            </div>

            {/* Botón para contactar con la organización */}
            {(project?.organization?.organization_whatsapp || project?.organization?.phone || project?.admin_contact?.organization_whatsapp) && (
              <motion.button
                onClick={() => {
                  setSelectedContact({
                    type: 'organization',
                    data: {
                      full_name: project?.organization?.name || 'Organización',
                      phone_number: project?.organization?.organization_whatsapp || project?.organization?.phone || project?.admin_contact?.organization_whatsapp,
                      organization_whatsapp: project?.organization?.organization_whatsapp || project?.admin_contact?.organization_whatsapp
                    }
                  });
                  setIsWhatsAppModalOpen(true);
                }}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-semibold text-xs shadow-md hover:shadow-lg transition-all hover:from-green-600 hover:to-green-700"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-3.5 h-3.5" />
                  <span>Contactar con la Organización</span>
                </div>
                <MessageCircle className="w-3.5 h-3.5" />
              </motion.button>
            )}

            {/* Botones para contactar con agentes */}
            {project?.assigned_agents && project.assigned_agents.length > 0 && (
              <div className="space-y-1.5">
                {project.assigned_agents.map((agent) => {
                  if (!agent.phone_number) return null;
                  
                  return (
                    <motion.button
                      key={agent.id}
                      onClick={() => {
                        setSelectedContact({
                          type: 'agent',
                          data: agent
                        });
                        setIsWhatsAppModalOpen(true);
                      }}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium text-xs shadow-sm hover:shadow-md transition-all hover:bg-gray-50 dark:hover:bg-gray-600"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {agent.profile_picture ? (
                          <img
                            src={agent.profile_picture}
                            alt={agent.full_name || 'Agente'}
                            className="w-6 h-6 rounded-full object-cover border border-gray-200 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900 flex items-center justify-center border border-gray-200 dark:border-gray-600 flex-shrink-0">
                            <Users className="w-3 h-3 text-brand-600 dark:text-brand-400" />
                          </div>
                        )}
                        <span className="truncate">
                          Contactar agente {agent.full_name || 'de Ventas'}
                        </span>
                      </div>
                      <MessageCircle className="w-3.5 h-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    </motion.button>
                  );
                })}
              </div>
            )}

            {/* Si no hay agentes ni organización, mostrar botón genérico */}
            {(!project?.assigned_agents || project.assigned_agents.length === 0) && 
             !project?.organization?.organization_whatsapp && 
             !project?.organization?.phone && 
             !project?.admin_contact?.organization_whatsapp && (
              <motion.button
                onClick={() => {
                  setSelectedContact(null);
                  setIsWhatsAppModalOpen(true);
                }}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-semibold text-xs shadow-md hover:shadow-lg transition-all hover:from-green-600 hover:to-green-700"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span>Consultar por WhatsApp</span>
              </motion.button>
            )}
          </div>
          )}
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <div className="w-14 h-14 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
              <Home className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Selecciona un lote
            </p>
            <p className="text-[10px] text-gray-500">
              Haz clic en un lote del mapa
            </p>
          </div>
        </div>
      )}

      {/* Gallery Modal */}
      {isGalleryOpen && lotImages.length > 0 && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={() => setIsGalleryOpen(false)}>
          <div className="relative w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            {/* Close */}
            <button
              onClick={() => setIsGalleryOpen(false)}
              className="absolute -top-10 right-0 text-white/80 hover:text-white transition-colors"
            >
              <XCircle className="w-7 h-7" />
            </button>

            {/* Main image */}
            <div className="relative rounded-lg overflow-hidden bg-black">
              <img
                src={lotImages[galleryIndex]}
                alt={`Foto ${galleryIndex + 1}`}
                className="w-full max-h-[70vh] object-contain"
              />

              {/* Counter */}
              <div className="absolute top-3 left-3 bg-black/60 text-white text-xs font-semibold px-2 py-1 rounded-md">
                {galleryIndex + 1} / {lotImages.length}
              </div>

              {/* Nav arrows */}
              {lotImages.length > 1 && (
                <>
                  <button
                    onClick={() => setGalleryIndex((prev) => (prev - 1 + lotImages.length) % lotImages.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <button
                    onClick={() => setGalleryIndex((prev) => (prev + 1) % lotImages.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {lotImages.length > 1 && (
              <div className="flex gap-2 mt-3 justify-center">
                {lotImages.map((url, i) => (
                  <button
                    key={i}
                    onClick={() => setGalleryIndex(i)}
                    className={cn(
                      'w-12 h-12 rounded-md overflow-hidden border-2 transition-all flex-shrink-0',
                      i === galleryIndex
                        ? 'border-orange-500 opacity-100'
                        : 'border-transparent opacity-60 hover:opacity-90'
                    )}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      <WhatsAppModal
        isOpen={isWhatsAppModalOpen}
        onClose={() => {
          setIsWhatsAppModalOpen(false);
          setSelectedContact(null);
        }}
        onSubmit={(formData) => {
          const message = formData.message;
          
          // Get phone number based on selected contact
          let phoneNumber = null;
          
          if (selectedContact) {
            if (selectedContact.type === 'organization') {
              phoneNumber = selectedContact.data.organization_whatsapp || 
                           selectedContact.data.phone_number;
            } else if (selectedContact.type === 'agent') {
              phoneNumber = selectedContact.data.phone_number;
            }
          }
          
          // Fallback to organization phone if no contact selected
          if (!phoneNumber) {
            phoneNumber = project?.organization?.organization_whatsapp || 
                         project?.organization?.phone ||
                         project?.admin_contact?.organization_whatsapp;
          }
          
          if (!phoneNumber) {
            console.error('No phone number available for WhatsApp');
            return;
          }
          
          // Clean phone number: remove all non-numeric characters except +
          const cleanPhone = phoneNumber.replace(/[^0-9+]/g, '');
          // Ensure it starts with +
          const whatsappNumber = cleanPhone.startsWith('+') ? cleanPhone : `+${cleanPhone}`;
          
          const whatsappUrl = `https://wa.me/${whatsappNumber.replace('+', '')}?text=${encodeURIComponent(message)}`;
          window.open(whatsappUrl, '_blank');
        }}
        project={project}
        lotProperties={lotProperties}
        lotId={lotId}
        projectId={project?.id}
        organizationPhone={selectedContact?.type === 'organization' 
          ? (selectedContact.data.organization_whatsapp || selectedContact.data.phone_number)
          : (project?.organization?.organization_whatsapp || project?.organization?.phone)}
        organizationId={project?.organization_id}
        apiUrl={brandConfig.apiUrl}
      />
    </div>
  );
};

export default LoteTab;
